import { type DefaultContext, mutationPromise } from '@egw/clients'
import { type EnrollmentHomeSubmitInput, type EnrollmentHomeData } from '../../../models'
import { createQueryHook, createMutationHook } from '../../../hooks'

// Submit output type for enrollment home data
export interface EnrollmentHomeSubmitOutput {
  __typename: 'SubmitEnrollmentSuccess' | 'SubmitEnrollmentError'
  path?: string
}

/**
 * Get Enrollment Home data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHomeData>
 * @example const response = await getEnrollmentHomeData()
 */
export const getEnrollmentHomeData = async (
  enrollmentHomeSubmitInput?: EnrollmentHomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHomeData',
      query: `mutation getEnrollmentHomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHomeData }
  return response.submitEnrollment
}

export const useGetEnrollmentHomeData = createQueryHook<EnrollmentHomeSubmitInput, EnrollmentHomeData>(
  getEnrollmentHomeData,
)

/**
 * Submit Enrollment Home data
 * @param enrollmentHomeSubmitInput - The enrollment home data to submit
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHomeSubmitOutput>
 * @example const response = await submitEnrollmentHomeData(\{ initiateApplication: true \})
 */
export const submitEnrollmentHomeData = async (
  enrollmentHomeSubmitInput: EnrollmentHomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHomeData',
      query: `mutation submitEnrollmentHomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHomeSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHomeData = createMutationHook<EnrollmentHomeSubmitInput, EnrollmentHomeSubmitOutput>(
  submitEnrollmentHomeData,
)
